import Dialog from "ui/elements/dialog/Dialog";
import {i18n} from "ui/i18n";
import Form from "ui/elements/form/Form";

/**
 * @typedef {object} T_ScalePickerResult
 * @property {number} scaleFrom
 * @property {number} scaleTo
 * @property {boolean} xAxis
 * @property {boolean} yAxis
 * @property {boolean} smooth
 */

/**
 * Layer Scale Picker
 */
export default class ScalePicker extends Dialog {
	/**
	 * @param {boolean} [rangeMode]
	 */
	constructor(rangeMode = false) {
		super({
			title: i18n('title.scale-picker'),
			buttons: [
				{
					caption: i18n('buttons.apply'),
					role: 'apply',
					icon: '/images/icons.svg#icon-svg-check'
				},
				{
					caption: i18n('buttons.close'),
					role: 'close'
				}
			]
		});

		this._rangeMode = rangeMode;

		/**
		 * @type {Form}
		 * @private
		 */
		this._form = null;

		/**
		 * @type {?function(res:T_ScalePickerResult|null)}
		 * @private
		 */
		this._onComplete = null;

		void this._buildForm();
	}

	/**
	 * @returns {Promise<T_ScalePickerResult|null>}
	 */
	getResultPromise() {
		return new Promise((resolve) => {
			this._onComplete = resolve;
		});
	}

	/**
	 * Handles click by button
	 *
	 * @override
	 * @param {string} role
	 * @returns {void}
	 * @protected
	 */
	_handleButton(role) {
		super._handleButton(role);
		if (role === 'apply') {
			let data = this._form.getValues();
			if (data !== null) {
				this._apply(data);
			}
		}
	}

	/**
	 * Applies form data and destroy Dialog Box if all ok
	 *
	 * @private
	 * @param {Object} data
	 * @returns {void}
	 */
	_apply(data) {
		if (this._onComplete) {
			this._onComplete({
				scaleFrom: parseInt(data.scaleFrom) / 100,
				scaleTo: parseInt(this._rangeMode ? data.scaleTo : data.scaleFrom) / 100,
				xAxis: data.xAxis,
				yAxis: data.yAxis,
				smooth: data.smooth,
			});
			this._onComplete = null;
			this.destroy();
		}
	}

	/**
	 * @private
	 * @returns {Promise<void>}
	 */
	async _buildForm() {
		let el = document.createElement('div');
		this._form = new Form({
			parent: el,
			onApply: this._apply.bind(this),
			values: {scaleFrom: 100, scaleTo: 100, xAxis: true, yAxis: true, smooth: false},
			fields: [
				{
					type: 'range200',
					name: 'scaleFrom',
					min: 10,
					max: 200,
					step: 5,
					caption: i18n(this._rangeMode ? 'inputs.layer.scale-from' : 'inputs.layer.scale'),
				},
				{
					type: this._rangeMode ? 'range200' : 'hidden',
					name: 'scaleTo',
					min: 10,
					max: 200,
					step: 5,
					caption: i18n('inputs.layer.scale-to'),
				},
				{
					type: 'checkbox',
					name: 'xAxis',
					caption: i18n('inputs.layer.x-axis'),
				},
				{
					type: 'checkbox',
					name: 'yAxis',
					caption: i18n('inputs.layer.y-axis'),
				},
				{
					type: 'checkbox',
					name: 'smooth',
					caption: i18n('inputs.layer.smooth'),
				},
			]
		});
		this.setContent(el);
	}

	/**
	 * Destroys the dialog box
	 *
	 * @returns {void}
	 */
	destroy() {
		if (this._onComplete !== null) {
			this._onComplete(null);
			this._onComplete = null;
		}
		super.destroy();
	}
}
